import PropTypes from "prop-types";
import React, { Component } from "react";
import { withTranslation } from "react-i18next";
import { connect } from "react-redux";
import { withAlert } from "react-alert";
import { withRouter } from "react-router-dom";
import {
  Checkbox,
  CircularProgress,
  Grid,
  Switch,
  Typography,
  Button
} from "@material-ui/core";
import styled, { css } from "styled-components";
import {
  KeyboardDatePicker,
  MuiPickersUtilsProvider
} from "@material-ui/pickers";
import DateFnsUtils from "@date-io/date-fns";
import ReactDataGrid from "@inovua/reactdatagrid-enterprise";
import moment from "moment";
import withTheme from "@material-ui/core/styles/withTheme";
import GridFrequencyService from "../../services/GridFrequencyService";
import ClassService from "../../services/ClassService";
import Group from "../../waybee-ui/Group";
import SearchableSelect from "../../waybee-ui/SearchableSelect";
import HeaderDetails from "../ClassGradesV2/components/HeaderDetails";
import HoverPopover from "../../components/HoverPopover";
import { getDataGridI18n } from "../../utils/getDataGridI18n";
import { getDataGridScrollProps } from "../../utils/getDataGridScrollProps";
import BackButton from "../../components/BackButton";
import fetchGradeJobQueue from "../../utils/fetchGradeJobQueue";
import { isJobInProgress } from "../../utils/jobHelpers";
import { clearLeaveGuard, setLeaveGuard } from "../../utils/leaveGuard";
import JobInProgressLoading from "../../components/JobInProgressLoading";
import isClassDiaryDemo from "../../demo/isClassDiaryDemo";
import { markDemoScheduleSlotFrequencyDone } from "../../demo/demoScheduleSlotStatus";
import {
  buildDoubleClassesByColumnId,
  buildSessionColumnsMeta,
  buildNestedFrequencyHeaderGroups,
  resolveFrequencyNestedDateGroup,
  hasAbsentInEarlierSessionSameDay,
  wasAbsentInEarlierSessionSameDay,
  countAbsencesInFrequencyRow,
  absencePercentInFrequencyRow,
  isCellAbsent,
  isCellAbsenceJustified
} from "../../utils/frequencySessionHelpers";
import {
  normalizeFrequencyHighlights,
  resolveFrequencyHighlightColor
} from "../../utils/gridFrequencyHighlights";
import { AbilityContext, Can } from "../../acl/can";
import GridFullscreen from "../../components/GridFullscreen";
import FrequencyAttendanceCell from "./components/FrequencyAttendanceCell";
import JustifyAbsenceDialog from "./components/JustifyAbsenceDialog";

const LoadContainer = styled.div`
  width: 100%;
  text-align: center;
  padding: 32px 8px;
`;

const CellText = styled.p`
  color: rgba(0, 0, 0, 0.5);
  font-size: 18px;
  text-overflow: ellipsis;
  overflow: hidden;
  line-height: ${props => (props.cellHeight ? 2.5 : "normal")};
  ${({ editable }) =>
    !!editable &&
    css`
      margin: 0;
      border: 1px solid rgba(0, 0, 0, 0.25);
      padding: 5px;
      border-radius: 5px;
      min-height: 30px;
    `}
`;

const CellContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  width: 100%;
  height: 100%;
  ${({ highlighted }) =>
    highlighted &&
    css`
      background-color: #fff8e1;
      border-radius: 4px;
    `}
`;

const NameCell = styled(CellText)`
  ${({ highlighted }) =>
    highlighted &&
    css`
      background-color: #fff3e0;
      border-left: 4px solid #ff9800;
      padding-left: 8px;
      margin: 0;
      font-weight: 500;
    `}
`;

const RollNum = styled.span`
  color: ${({ theme }) => theme.color.primary.main};
  margin-right: 12px;
`;

const Header = styled(Grid)`
  background-color: white;
  border-right: 1px solid #e4e3e2;
  border-top: 1px solid #e4e3e2;
  border-left: 1px solid #e4e3e2;
  display: flex;
  justify-content: start;
  align-items: center;
  padding: 10px;
`;

const PeriodSummaryBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 24px;
  width: 100%;
  box-sizing: border-box;
  background-color: #fafafa;
  border-right: 1px solid #e4e3e2;
  border-bottom: 1px solid #e4e3e2;
  border-left: 1px solid #e4e3e2;
  padding: 10px 16px;
`;

const PeriodSummaryItem = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 8px 16px;
`;

const PeriodSummaryPeriod = styled(Typography)`
  && {
    font-weight: 600;
    margin: 0;
  }
`;

const PeriodSummaryMetric = styled(Typography)`
  && {
    margin: 0;
    color: rgba(0, 0, 0, 0.65);
  }
`;

function withFrequencyCellHighlight(column, cellKind, highlights) {
  return {
    ...column,
    onRender: (cellProps, { data }) => {
      const color = resolveFrequencyHighlightColor(highlights, cellKind, {
        desistente: data && data[0] && data[0].desistente
      });
      // eslint-disable-next-line no-param-reassign
      cellProps.style = {
        ...(cellProps.style || {}),
        background: color || undefined
      };
    }
  };
}

function buildCellJustification(reason) {
  if (!reason) {
    return { idJustificativaFalta: "", justificada: "" };
  }
  return {
    idJustificativaFalta: String(reason.idJustificativaFalta),
    justificada: reason.abona ? "1" : "0"
  };
}

class GridFrequency extends Component {
  constructor(props) {
    super(props);
    this.state = {
      loadingFilters: false,
      courses: [],
      filter: {},
      selectedDate: {
        dateFrom: moment()
          .startOf("week")
          .format("YYYY-MM-DDTHH:mm:ss"),
        dateTo: moment().format("YYYY-MM-DDTHH:mm:ss")
      },
      dataSource: [],
      columns: [],
      groups: [],
      pendingChanges: {},
      loadingSaveFrequency: false,
      loadingTable: false,
      gridRef: null,
      isClassDoubleActive: false,
      showDoubleClasses: false,
      doubleClasses: {},
      sessionColumnsMeta: [],
      loadingJobs: [],
      processedJobs: [],
      loadingMessage: "",
      periodSummaries: [],
      highlights: [],
      justificationReasons: [],
      justifyDialog: {
        open: false,
        studentId: null,
        studentName: "",
        column: null,
        currentReasonId: ""
      }
    };
  }

  componentDidMount() {
    this.initFrequencyPage();
  }

  componentDidUpdate(prevProps) {
    const { pendingChanges } = this.state;
    setLeaveGuard(() => Object.keys(pendingChanges || {}).length > 0);

    const {
      jobsState: { jobs }
    } = this.props;
    const { filter, loadingJobs, processedJobs } = this.state;
    const currentClass = localStorage.getItem("class");
    const pendingJobKey = this.getPendingFrequencyJobKey(currentClass, filter);

    if (!pendingJobKey || loadingJobs.length === 0 || jobs.length < 1) {
      return;
    }

    jobs.forEach(job => {
      if (job.name !== "save_frequency") {
        return;
      }

      const previousJob = prevProps.jobsState.jobs.find(
        prevJob => prevJob.id === job.id
      );
      const hasProcessed = processedJobs.includes(job.id);

      if (
        !hasProcessed &&
        (job.status === "success" || job.status === "error") &&
        previousJob &&
        job.status !== previousJob.status &&
        job.classId === currentClass &&
        filter.course &&
        job.course === filter.course.id &&
        job.period === "Frequência"
      ) {
        this.handleFrequencyJobCompleted(job.id, pendingJobKey);
      }
    });
  }

  componentWillUnmount() {
    clearLeaveGuard();
  }

  getPendingFrequencyJobKey = (classId, filter) => {
    if (!classId || !filter.course) {
      return null;
    }

    return `${classId}${filter.course.id}Frequência`;
  };

  handleFrequencyJobCompleted = (jobId, pendingJobKey) => {
    this.setState(
      prev => ({
        loadingJobs: prev.loadingJobs.filter(
          jobKey => jobKey !== pendingJobKey
        ),
        processedJobs: [...prev.processedJobs, jobId]
      }),
      () => {
        const { filter } = this.state;
        if (filter.course) {
          this.getGridFrequency();
        }
      }
    );
  };

  getFrequencyJobQueue = () => {
    const { jobsState, t } = this.props;
    const { filter, loadingJobs } = this.state;
    const currentClass = localStorage.getItem("class");
    const pendingJobKey = this.getPendingFrequencyJobKey(currentClass, filter);

    if (!pendingJobKey) {
      return;
    }

    jobsState.jobs.forEach(job => {
      if (
        isJobInProgress(job) &&
        job.name === "save_frequency" &&
        job.classId === currentClass &&
        filter.course &&
        job.course === filter.course.id &&
        job.period === "Frequência" &&
        !loadingJobs.includes(pendingJobKey)
      ) {
        this.setState(prevState => ({
          loadingJobs: [...prevState.loadingJobs, pendingJobKey],
          loadingMessage: t("frequency.jobInProgressCourse", {
            course: filter.course.name || job.course
          })
        }));
      }
    });
  };

  getCourses = async () => {
    const { match, alert, t } = this.props;

    this.setState({ loadingFilters: true });

    const classId = match.params.id;
    try {
      const disciplines = await ClassService.getDisciplines(classId);
      this.setState(
        prevState => ({
          courses: disciplines,
          loadingFilters: false,
          filter:
            disciplines && disciplines.length
              ? { ...prevState.filter, course: disciplines[0] }
              : prevState.filter
        }),
        () => {
          if (disciplines && disciplines.length) {
            this.getGridFrequency();
          }
        }
      );
    } catch (e) {
      console.error(e);
      alert.show(t("common.messages.errorFetchDisciplines"), {
        title: t("common.actions.error")
      });
      this.setState({ loadingFilters: false });
    }
  };

  /**
   * @param {number|string|undefined} [idTurmaDiscOverride] — use após trocar disciplina (estado ainda pode não ter `filter.course`).
   */
  getGridFrequency = async idTurmaDiscOverride => {
    const {
      filter,
      selectedDate,
      isClassDoubleActive,
      highlights
    } = this.state;
    const { dateFrom, dateTo } = selectedDate;
    const { theme, t, i18n } = this.props;
    const { context } = this;
    this.setState({ loadingTable: true });

    const courseId =
      idTurmaDiscOverride != null
        ? idTurmaDiscOverride
        : filter?.course?.idTurmaDisc;

    this.getFrequencyJobQueue();

    if (courseId == null) {
      this.setState({
        columns: [],
        dataSource: [],
        groups: [],
        pendingChanges: {},
        sessionColumnsMeta: [],
        doubleClasses: {},
        periodSummaries: [],
        loadingTable: false
      });
      return;
    }

    const raw = await GridFrequencyService.getFrequency(
      courseId,
      dateFrom,
      dateTo
    );

    const frequencyGrid =
      raw != null && !Array.isArray(raw) && typeof raw === "object"
        ? {
            groups: raw.groups || [],
            headers: raw.headers || [],
            lines: raw.lines || [],
            periodSummaries: Array.isArray(raw.periodSummaries)
              ? raw.periodSummaries
              : []
          }
        : { groups: [], headers: [], lines: [], periodSummaries: [] };

    const dateLocale = i18n.language === "en" ? "en" : "pt-br";
    const { periodGroups, dateGroups } = buildNestedFrequencyHeaderGroups(
      frequencyGrid.headers,
      frequencyGrid.groups,
      dateLocale
    );
    const groups = [
      ...periodGroups.map(group => ({
        ...group,
        header: <HeaderDetails title={group.header} />
      })),
      ...dateGroups.map(group => ({
        ...group,
        header: <HeaderDetails title={group.header} />
      }))
    ];

    const sessionColumnsMeta = buildSessionColumnsMeta(frequencyGrid.headers);
    const doubleClasses = buildDoubleClassesByColumnId(frequencyGrid.headers);

    const columns = frequencyGrid.headers.map(column => {
      return withFrequencyCellHighlight(
        {
          id: column.id,
          name: column.id,
          header: column.header,
          group: resolveFrequencyNestedDateGroup(column),
          codEtapa: column.codEtapa,
          aulasDadas: column.aulasDadas,
          defaultWidth: 210,
          showColumnMenuTool: false,
          rendersInlineEditor: true,
          headerProps: {
            style:
              column.double === "true" && isClassDoubleActive
                ? {
                    borderBottom: `2px solid ${theme.palette.primary.main}`
                  }
                : {
                    borderBottom: "none"
                  }
          },
          render: ({ data }, { cellProps }) => {
            const columnData = Array.isArray(data)
              ? Object.fromEntries(
                  data.map(item => [item.columnId, { ...item }])
                )
              : {};

            if (columnData[column.id] && columnData[column.id].totalAbsences) {
              return (
                <CellText style={{ display: "flex", justifyContent: "center" }}>
                  {columnData[column.id].totalAbsences}
                </CellText>
              );
            }

            /* No payload, `absence === "true"` = presente (checkbox marcado); `"false"` = ausente. */
            const cell = columnData[column.id];
            const isAbsent = !!cell && String(cell.absence) === "false";
            const isPresent = !isAbsent;

            const checkboxValue = cellProps.editProps.inEdit
              ? cellProps.editProps.value === "true"
              : isPresent;

            const controlledCheckboxValue =
              checkboxValue !== undefined ? checkboxValue : true;

            const missedEarlierSameDay = wasAbsentInEarlierSessionSameDay(
              data,
              column.id,
              sessionColumnsMeta
            );
            const canSave = context.can("save", "gridFrequency");
            const studentId = data[0] && data[0].studentId;
            const studentName = data[0] && data[0].name;

            return (
              <FrequencyAttendanceCell
                cellId={column.id + studentId}
                checked={controlledCheckboxValue}
                disabled={!canSave}
                highlighted={missedEarlierSameDay}
                canJustify={canSave && context.can("justify", "gridFrequency")}
                justification={this.getCellJustification(cell)}
                onCheckboxClick={e =>
                  this.handleOnClickSwitch(e, cellProps, column)}
                onJustifyClick={() =>
                  this.openJustifyAbsenceDialog({
                    studentId,
                    studentName,
                    column,
                    cell
                  })}
              />
            );
          },
          renderHeader: ({ value }) => {
            const splitString = String(value || "").split(";");
            const timeFrom = splitString[splitString.length - 3] || "";
            const timeTo = splitString[splitString.length - 2] || "";
            const scheduleLabel =
              column.schedule ||
              (column.horaInicial && column.horaFinal
                ? `${column.horaInicial} - ${column.horaFinal}`
                : `${timeFrom} - ${timeTo}`);

            return (
              <CellContainer>
                <HeaderDetails
                  title={scheduleLabel}
                  rowHeight={80}
                  hasDetails
                  column={column}
                  weight="400"
                />
              </CellContainer>
            );
          }
        },
        "attendance",
        highlights
      );
    });

    const tableEl = document.querySelector(".studentIdFrequency");
    columns.unshift(
      withFrequencyCellHighlight(
        {
          name: "studentId",
          header: t("frequency.col.ra"),
          editable: false,
          locked: true,
          group: "data",
          showColumnMenuTool: false,
          defaultWidth: tableEl ? tableEl.offsetWidth * 0.1 + 10 : 110,
          minWidth: 90,
          maxWidth: 130,
          render: ({ data }) => <CellText>{data[0].studentId}</CellText>,
          renderHeader: ({ value }) => <HeaderDetails title={value} />
        },
        "ra",
        highlights
      )
    );
    columns.unshift(
      withFrequencyCellHighlight(
        {
          name: "name",
          header: t("frequency.col.student"),
          editable: false,
          locked: true,
          sortable: true,
          group: "data",
          showColumnMenuLockOptions: false,
          showColumnMenuGroupOptions: false,
          showColumnMenuFilterOptions: false,
          defaultWidth: tableEl ? tableEl.offsetWidth * 0.35 : 350,
          render: ({ data }, { column }) => {
            const missedEarlier = hasAbsentInEarlierSessionSameDay(
              data,
              sessionColumnsMeta
            );
            return (
              <HoverPopover key={data.id} id={data.id} value={data[0].name}>
                <NameCell highlighted={missedEarlier}>
                  <RollNum>{column.rollCallNumber}</RollNum>
                  {data[0].name}
                </NameCell>
              </HoverPopover>
            );
          },
          renderHeader: ({ value }) => <HeaderDetails title={value} />
        },
        "name",
        highlights
      )
    );

    columns.splice(
      2,
      0,
      withFrequencyCellHighlight(
        {
          name: "totalAbsencesPeriod",
          header: t("frequency.col.totalAbsences"),
          editable: false,
          locked: true,
          group: "data",
          showColumnMenuTool: false,
          defaultWidth: 88,
          minWidth: 72,
          maxWidth: 100,
          render: ({ data }) => (
            <CellText style={{ display: "flex", justifyContent: "center" }}>
              {countAbsencesInFrequencyRow(data)}
            </CellText>
          ),
          renderHeader: ({ value }) => <HeaderDetails title={value} compact />
        },
        "totalAbsencesPeriod",
        highlights
      )
    );

    columns.splice(
      3,
      0,
      withFrequencyCellHighlight(
        {
          name: "absencesPercentPeriod",
          header: t("frequency.col.absencesPercent"),
          editable: false,
          locked: true,
          group: "data",
          showColumnMenuTool: false,
          defaultWidth: 96,
          minWidth: 88,
          maxWidth: 110,
          render: ({ data }) => (
            <CellText style={{ display: "flex", justifyContent: "center" }}>
              {`${absencePercentInFrequencyRow(data)}%`}
            </CellText>
          ),
          renderHeader: ({ value }) => <HeaderDetails title={value} compact />
        },
        "absencesPercentPeriod",
        highlights
      )
    );

    this.setState({
      columns,
      dataSource: frequencyGrid.lines,
      groups,
      pendingChanges: {},
      sessionColumnsMeta,
      doubleClasses,
      periodSummaries: frequencyGrid.periodSummaries,
      loadingTable: false
    });
  };

  setFilter(field, value) {
    const { filter } = this.state;
    this.setState({
      filter: {
        ...filter,
        [field]: value
      }
    });
  }

  getCellJustification = cell => {
    if (!isCellAbsenceJustified(cell)) {
      return null;
    }
    const { justificationReasons } = this.state;
    const reason = justificationReasons.find(
      item =>
        String(item.idJustificativaFalta) === String(cell.idJustificativaFalta)
    );
    return {
      descricao: reason ? reason.descricao : "",
      abona: String(cell.justificada) === "1"
    };
  };

  openJustifyAbsenceDialog = ({ studentId, studentName, column, cell }) => {
    this.setState({
      justifyDialog: {
        open: true,
        studentId,
        studentName: studentName || "",
        column,
        currentReasonId: isCellAbsenceJustified(cell)
          ? String(cell.idJustificativaFalta)
          : ""
      }
    });
  };

  closeJustifyAbsenceDialog = () => {
    this.setState(prevState => ({
      justifyDialog: {
        ...prevState.justifyDialog,
        open: false
      }
    }));
  };

  buildJustifyLessonLabel = column => {
    if (!column) {
      return "";
    }
    const date = moment(column.date).format("DD/MM/YYYY");
    return column.schedule ? `${date} · ${column.schedule}` : date;
  };

  buildPendingAttendanceEntry = (studentId, columnId, value) => {
    const { columns } = this.state;
    const parts = columnId.split(";");
    const columnMeta = columns.find(col => col.id === columnId);

    return {
      studentId,
      idTurmaDisc: parts[1],
      idSchedule: parts[2],
      value,
      date: this.formatSelectedDate({
        date: parts[parts.length - 1],
        startTime: parts[parts.length - 3]
      }),
      columnId,
      codEtapa: parts[3] || (columnMeta && columnMeta.codEtapa) || null,
      aulasDadas: (columnMeta && columnMeta.aulasDadas) || null
    };
  };

  stageAbsenceJustification = reason => {
    const { justifyDialog, dataSource, pendingChanges } = this.state;
    const { studentId, column } = justifyDialog;
    const justification = buildCellJustification(reason);
    const nextPending = { ...pendingChanges };

    const nextDataSource = dataSource.map(row => {
      if (!Array.isArray(row) || !row[0]) {
        return row;
      }
      if (String(row[0].studentId) !== String(studentId)) {
        return row;
      }

      const absentColumnIds = this.getColumnIdsForBulkPresent(
        column.id
      ).filter(columnId =>
        row.some(item => item.columnId === columnId && isCellAbsent(item))
      );

      absentColumnIds.forEach(columnId => {
        const changeKey = `${studentId}::${columnId}`;
        nextPending[changeKey] = {
          ...(nextPending[changeKey] ||
            this.buildPendingAttendanceEntry(studentId, columnId, "A")),
          idJustificativaFalta: justification.idJustificativaFalta
        };
      });

      return row.map(item =>
        item && absentColumnIds.includes(item.columnId)
          ? { ...item, ...justification }
          : item
      );
    });

    this.setState(prevState => ({
      dataSource: nextDataSource,
      pendingChanges: nextPending,
      justifyDialog: { ...prevState.justifyDialog, open: false }
    }));
  };

  loadJustificationReasons = async () => {
    try {
      const justificationReasons = await GridFrequencyService.getAbsenceJustificationReasons();
      this.setState({ justificationReasons });
    } catch (e) {
      console.error(e);
      this.setState({ justificationReasons: [] });
    }
  };

  initFrequencyPage = async () => {
    const reasonsLoading = this.loadJustificationReasons();
    try {
      const settings = await GridFrequencyService.getFrequencySettings();
      await reasonsLoading;
      const showDoubleClasses = settings.showDoubleClasses === "1";
      const highlights = normalizeFrequencyHighlights(settings.highlights);
      this.setState(
        {
          showDoubleClasses,
          isClassDoubleActive: showDoubleClasses,
          highlights
        },
        () => this.getCourses()
      );
    } catch (e) {
      console.error(e);
      await reasonsLoading;
      this.setState(
        {
          showDoubleClasses: false,
          isClassDoubleActive: false,
          highlights: []
        },
        () => this.getCourses()
      );
    }
  };

  handleOnClickSwitch = (e, cellProps, column) => {
    const { doubleClasses, isClassDoubleActive } = this.state;
    const relatedColumns = doubleClasses[column.id];
    const targetDoubleAttribute = e.target.getAttribute("doubleRelatedCell");

    if (isClassDoubleActive) {
      if (relatedColumns && targetDoubleAttribute !== "true") {
        relatedColumns.forEach(doubleClass => {
          if (doubleClass.id === column.id) {
            return;
          }
          const doubleCellRelated =
            doubleClass.id + cellProps.data[0].studentId;
          const cellDouble = document.querySelector(
            `[data-id='${doubleCellRelated}'] input`
          );

          if (cellDouble && cellDouble.checked !== e.target.checked) {
            cellDouble.setAttribute("doubleRelatedCell", true);
            requestAnimationFrame(() => {
              cellDouble.click();
            });
          }
        });
      } else if (relatedColumns) {
        e.target.setAttribute("doubleRelatedCell", false);
      }
    }

    e.target.value = e.target.checked;
    cellProps.editProps.onChange(e);
  };

  changeCourse = async course => {
    const { match } = this.props;

    const classId = match.params.id;
    this.setState({ loadingFilters: true });
    this.setFilter("course", course);
    await ClassService.getDisciplines(classId);
    await this.getGridFrequency(course?.idTurmaDisc);
    this.setState({ loadingFilters: false });
  };

  handleDateFromChange = async date => {
    if (!date) return;

    const formattedDate = moment(date).format("YYYY-MM-DDTHH:mm:ss");

    await this.setState(prevState => ({
      selectedDate: {
        ...prevState.selectedDate,
        dateFrom: formattedDate
      }
    }));

    this.getGridFrequency();
  };

  handleDateToChange = async date => {
    if (!date) return;

    const formattedDate = moment(date).format("YYYY-MM-DDTHH:mm:ss");

    await this.setState(prevState => ({
      selectedDate: {
        ...prevState.selectedDate,
        dateTo: formattedDate
      }
    }));

    this.getGridFrequency();
  };

  scrollTo = index => {
    const { gridRef, columns } = this.state;
    const columnId = parseInt(Object.keys(index)[0].split(",")[1], 10);
    const scrollTo = columnId + 4;

    if (scrollTo <= columns.length) {
      gridRef.current.scrollToColumn(scrollTo, { duration: 100 });
    }
  };

  formatSelectedDate = schedule => {
    const [hour, minute] = schedule.startTime.split(":");
    return moment(schedule.date)
      .set({ hour, minute })
      .format("YYYY-MM-DD HH:mm:SS");
  };

  onEditComplete = async ({ value, columnId, rowIndex, data }) => {
    const { dataSource, pendingChanges, columns } = this.state;

    const columnData = columnId.split(";");
    const studentId = data[0] && data[0].studentId;
    const schedule = {
      date: columnData[columnData.length - 1],
      startTime: columnData[columnData.length - 3]
    };
    const editedDataSource = dataSource.map(row =>
      Array.isArray(row) ? [...row] : row
    );
    const currentColumn = editedDataSource[rowIndex] || [];

    const columnIndex = currentColumn.findIndex(
      item => item.columnId === columnId
    );

    const columnMeta = columns.find(col => col.id === columnId);
    const codEtapa =
      columnData[3] || (columnMeta && columnMeta.codEtapa) || null;
    const aulasDadas = (columnMeta && columnMeta.aulasDadas) || null;

    const studentAttendanceData = {
      columnId,
      absence: value === "true" ? "true" : "false",
      idHorarioTurma: columnData[2],
      idTurmaDisc: columnData[1],
      studentId,
      codEtapa
    };

    if (columnIndex !== -1) {
      currentColumn[columnIndex] = {
        ...currentColumn[columnIndex],
        ...studentAttendanceData
      };
    } else {
      currentColumn.push(studentAttendanceData);
    }

    const changeKey = `${studentId}::${columnId}`;
    const pendingEntry = {
      studentId,
      idTurmaDisc: studentAttendanceData.idTurmaDisc,
      idSchedule: studentAttendanceData.idHorarioTurma,
      value: studentAttendanceData.absence === "true" ? "P" : "A",
      date: this.formatSelectedDate(schedule),
      columnId,
      codEtapa,
      aulasDadas
    };
    const previousPending = pendingChanges[changeKey];
    if (previousPending && "idJustificativaFalta" in previousPending) {
      pendingEntry.idJustificativaFalta = previousPending.idJustificativaFalta;
    }

    this.setState({
      dataSource: editedDataSource,
      pendingChanges: {
        ...pendingChanges,
        [changeKey]: pendingEntry
      }
    });
  };

  getColumnIdsForBulkPresent = columnId => {
    const { doubleClasses, isClassDoubleActive } = this.state;
    const columnIds = [columnId];

    if (isClassDoubleActive && doubleClasses[columnId]) {
      doubleClasses[columnId].forEach(classColumn => {
        if (!columnIds.includes(classColumn.id)) {
          columnIds.push(classColumn.id);
        }
      });
    }

    return columnIds;
  };

  markAllPresentForColumn = async columnId => {
    const { dataSource, filter, columns } = this.state;
    const { alert, match, t } = this.props;
    const columnIds = this.getColumnIdsForBulkPresent(columnId);
    const classId = match.params.id;

    const absences = [];
    const editedDataSource = dataSource.map(row => {
      const newRow = Array.isArray(row) ? [...row] : row;
      const studentId = newRow[0] && newRow[0].studentId;
      if (!studentId) {
        return newRow;
      }

      columnIds.forEach(targetColumnId => {
        const parts = targetColumnId.split(";");
        const columnMeta = columns.find(col => col.id === targetColumnId);
        const schedule = {
          date: parts[parts.length - 1],
          startTime: parts[parts.length - 3]
        };
        const attendance = {
          columnId: targetColumnId,
          absence: "true",
          idHorarioTurma: parts[2],
          idTurmaDisc: parts[1],
          studentId,
          codEtapa: parts[3] || (columnMeta && columnMeta.codEtapa) || null
        };
        const columnIndex = newRow.findIndex(
          item => item.columnId === targetColumnId
        );

        if (columnIndex !== -1) {
          newRow[columnIndex] = {
            ...newRow[columnIndex],
            ...attendance
          };
        } else {
          newRow.push(attendance);
        }

        absences.push({
          studentId,
          idTurmaDisc: parts[1],
          idSchedule: parts[2],
          value: "P",
          date: this.formatSelectedDate(schedule),
          columnId: targetColumnId,
          codEtapa: attendance.codEtapa,
          aulasDadas: (columnMeta && columnMeta.aulasDadas) || null
        });
      });

      return newRow;
    });

    this.setState({ loadingSaveFrequency: true });

    try {
      await GridFrequencyService.saveFrequencyJob({
        filter: {
          course: filter.course,
          period: "Frequência"
        },
        classId,
        absences
      });
      alert.show(t("common.messages.processingRequest"), {
        title: t("frequency.sent")
      });
      const currentClass = localStorage.getItem("class");
      const pendingJobKey = this.getPendingFrequencyJobKey(
        currentClass,
        filter
      );
      this.setState(
        prevState => {
          const nextPending = { ...prevState.pendingChanges };
          absences.forEach(item => {
            delete nextPending[`${item.studentId}::${item.columnId}`];
          });
          return {
            dataSource: editedDataSource,
            pendingChanges: nextPending,
            loadingJobs:
              pendingJobKey && !prevState.loadingJobs.includes(pendingJobKey)
                ? [...prevState.loadingJobs, pendingJobKey]
                : prevState.loadingJobs,
            loadingMessage: t("frequency.jobInProgressCourse", {
              course:
                (filter.course && filter.course.name) ||
                (filter.course && filter.course.id) ||
                ""
            })
          };
        },
        () => {
          fetchGradeJobQueue();
        }
      );
      if (isClassDiaryDemo()) {
        const demoSlotKey = new URLSearchParams(window.location.search).get(
          "demoSlotKey"
        );
        if (demoSlotKey) {
          markDemoScheduleSlotFrequencyDone(decodeURIComponent(demoSlotKey));
        }
      }
    } catch (e) {
      console.error(e);
      const errMsg =
        e && typeof e.message === "string" ? e.message : String(e || "");
      if (!errMsg.includes("timeout")) {
        alert.show(t("frequency.massError"), {
          title: t("common.actions.error")
        });
      }
    } finally {
      this.setState({ loadingSaveFrequency: false });
    }
  };

  saveFrequency = async () => {
    const { alert, match, t } = this.props;
    const { pendingChanges, filter } = this.state;
    const absences = Object.values(pendingChanges);
    const classId = match.params.id;

    if (!absences.length) {
      alert.show(t("frequency.noChanges"), {
        title: t("frequency.noChangesTitle")
      });
      return;
    }

    this.setState({ loadingSaveFrequency: true });

    try {
      await GridFrequencyService.saveFrequencyJob({
        filter: {
          course: filter.course,
          period: "Frequência"
        },
        classId,
        absences
      });
      alert.show(t("common.messages.processingRequest"), {
        title: t("frequency.sent")
      });
      const currentClass = localStorage.getItem("class");
      const pendingJobKey = this.getPendingFrequencyJobKey(
        currentClass,
        filter
      );
      this.setState(
        prevState => ({
          pendingChanges: {},
          loadingJobs:
            pendingJobKey && !prevState.loadingJobs.includes(pendingJobKey)
              ? [...prevState.loadingJobs, pendingJobKey]
              : prevState.loadingJobs,
          loadingMessage: t("frequency.jobInProgressCourse", {
            course:
              (filter.course && filter.course.name) ||
              (filter.course && filter.course.id) ||
              ""
          })
        }),
        () => {
          fetchGradeJobQueue();
        }
      );
      if (isClassDiaryDemo()) {
        const demoSlotKey = new URLSearchParams(window.location.search).get(
          "demoSlotKey"
        );
        if (demoSlotKey) {
          markDemoScheduleSlotFrequencyDone(decodeURIComponent(demoSlotKey));
        }
      }
    } catch (e) {
      console.error(e);
      const errMsg =
        e && typeof e.message === "string" ? e.message : String(e || "");
      if (!errMsg.includes("timeout")) {
        alert.show(t("frequency.saveError", { err: errMsg }), {
          title: t("common.actions.error")
        });
      }
    } finally {
      this.setState({ loadingSaveFrequency: false });
    }
  };

  handleAttachedClasses = async () => {
    await this.setState(prevState => ({
      isClassDoubleActive: !prevState.isClassDoubleActive
    }));
    await this.getGridFrequency();
  };

  render() {
    const {
      courses,
      filter,
      loadingFilters,
      selectedDate,
      columns,
      groups,
      loadingTable,
      dataSource,
      isClassDoubleActive,
      showDoubleClasses,
      sessionColumnsMeta,
      pendingChanges,
      loadingSaveFrequency,
      loadingJobs,
      loadingMessage,
      periodSummaries,
      justifyDialog,
      justificationReasons
    } = this.state;
    const hasPendingChanges = Object.keys(pendingChanges).length > 0;
    const hasAbsentEarlierHighlight = dataSource.some(row =>
      hasAbsentInEarlierSessionSameDay(row, sessionColumnsMeta)
    );
    const currentClass = localStorage.getItem("class");
    const pendingJobKey = this.getPendingFrequencyJobKey(currentClass, filter);
    const isFrequencyJobPending =
      !!pendingJobKey && loadingJobs.includes(pendingJobKey);
    const { context } = this;
    const canSaveFrequency = context.can("save", "gridFrequency");

    const { match, t } = this.props;
    const classId = match.params.id;

    const menu = menuProps => {
      menuProps.items.splice(-2, 2);
    };

    return (
      <MuiPickersUtilsProvider utils={DateFnsUtils}>
        <Grid container spacing={2}>
          <BackButton to={`/classes/${classId}`} />
          <Grid item xs={12}>
            <Group fullWidth>
              {loadingFilters ? (
                <LoadContainer>
                  <CircularProgress />
                </LoadContainer>
              ) : (
                <Grid
                  container
                  spacing={2}
                  alignItems="center"
                  justify="center"
                >
                  <Grid item xs={6}>
                    <SearchableSelect
                      label={t("common.labels.discipline")}
                      options={courses}
                      value={filter.course || null}
                      onChange={this.changeCourse}
                      disableClearable
                      getOptionLabel={o => o.name || ""}
                      getOptionSelected={(a, b) =>
                        String(a.idTurmaDisc) === String(b.idTurmaDisc)}
                      noOptionsText={t("common.messages.noDiscipline")}
                    />
                  </Grid>
                  <Grid item xs={2} />
                  <Grid item xs={2}>
                    <KeyboardDatePicker
                      disableToolbar
                      variant="inline"
                      format="dd/MM/yyyy"
                      margin="normal"
                      id="date-picker-inline"
                      label={t("frequency.dateFrom")}
                      value={selectedDate.dateFrom}
                      onChange={this.handleDateFromChange}
                      maxDate={new Date()}
                      disabled={isFrequencyJobPending}
                    />
                  </Grid>
                  <Grid item xs={2}>
                    <KeyboardDatePicker
                      disableToolbar
                      variant="inline"
                      format="dd/MM/yyyy"
                      margin="normal"
                      id="date-picker-inline"
                      label={t("frequency.dateTo")}
                      value={selectedDate.dateTo}
                      onChange={this.handleDateToChange}
                      maxDate={new Date()}
                      disabled={isFrequencyJobPending}
                    />
                  </Grid>
                </Grid>
              )}
            </Group>
          </Grid>
          {!loadingFilters && (
            <Grid item xs={12}>
              {loadingTable || isFrequencyJobPending ? (
                <JobInProgressLoading
                  inProgress={isFrequencyJobPending}
                  message={loadingMessage || t("frequency.jobInProgress")}
                />
              ) : (
                <GridFullscreen
                  minHeight={550}
                  renderToolbar={trigger => (
                    <Header
                      item
                      xs={12}
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center"
                      }}
                    >
                      <div style={{ display: "flex", alignItems: "center" }}>
                        <Checkbox
                          checked={false}
                          color="primary"
                          style={{ padding: 4 }}
                        />
                        <Typography
                          style={{ margin: 0, padding: "0 16px 0 0" }}
                          variant="h6"
                        >
                          {t("frequency.absent")}
                        </Typography>
                        <Checkbox
                          checked
                          color="primary"
                          style={{ padding: 4 }}
                        />
                        <Typography
                          style={{ margin: 0, padding: "0 16px 0 0" }}
                          variant="h6"
                        >
                          {t("frequency.present")}
                        </Typography>
                        {hasAbsentEarlierHighlight && (
                          <Typography
                            style={{
                              margin: 0,
                              marginLeft: 16,
                              padding: "4px 10px",
                              backgroundColor: "#fff3e0",
                              borderLeft: "4px solid #ff9800",
                              fontSize: 14
                            }}
                            variant="body2"
                          >
                            {t("frequency.absentEarlierHint")}
                          </Typography>
                        )}
                        {showDoubleClasses && (
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              marginLeft: 16,
                              flexShrink: 0
                            }}
                          >
                            <Switch
                              checked={isClassDoubleActive}
                              onClick={this.handleAttachedClasses}
                              color="primary"
                            />
                            <Typography
                              style={{ margin: 0, padding: "0 12px 0 12px" }}
                              variant="h6"
                            >
                              {t("frequency.markDoubleClasses")}
                            </Typography>
                          </div>
                        )}
                      </div>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 12
                        }}
                      >
                        {trigger}
                        <Can I="save" a="gridFrequency">
                          <Button
                            variant="contained"
                            color="primary"
                            disabled={
                              !hasPendingChanges || loadingSaveFrequency
                            }
                            onClick={this.saveFrequency}
                          >
                            {loadingSaveFrequency
                              ? t("common.actions.sending")
                              : t("common.actions.save")}
                          </Button>
                        </Can>
                      </div>
                    </Header>
                  )}
                >
                  {({ isFullscreen }) => (
                    <div
                      style={
                        isFullscreen
                          ? {
                              display: "flex",
                              flexDirection: "column",
                              height: "100%",
                              minHeight: 0
                            }
                          : undefined
                      }
                    >
                      {periodSummaries.length > 0 && (
                        <PeriodSummaryBar>
                          {periodSummaries.map(period => (
                            <PeriodSummaryItem
                              key={period.codEtapa || period.name}
                            >
                              <PeriodSummaryPeriod variant="subtitle1">
                                {period.name || period.codEtapa}
                              </PeriodSummaryPeriod>
                              <PeriodSummaryMetric variant="body2">
                                {t("frequency.aulasPrevistas", {
                                  count: period.aulasPrevistas ?? "—"
                                })}
                              </PeriodSummaryMetric>
                              <PeriodSummaryMetric variant="body2">
                                {t("frequency.aulasDadas", {
                                  count: period.aulasDadas ?? "—"
                                })}
                              </PeriodSummaryMetric>
                            </PeriodSummaryItem>
                          ))}
                        </PeriodSummaryBar>
                      )}
                      <ReactDataGrid
                        columns={columns}
                        groups={groups}
                        dataSource={dataSource}
                        rowHeight={50}
                        style={
                          isFullscreen
                            ? { height: "100%", minHeight: 0, flex: 1 }
                            : { minHeight: 550 }
                        }
                        licenseKey={process.env.REACT_APP_DATA_GRID_LICENSE_KEY}
                        idProperty="studentIdFrequency"
                        i18n={getDataGridI18n(t)}
                        scrollProps={getDataGridScrollProps()}
                        reorderColumns={false}
                        onColumnLockedChange={e => e}
                        onReady={e => this.setState({ gridRef: e })}
                        onEditComplete={this.onEditComplete}
                        onEditValueChange={this.onEditComplete}
                        editable={canSaveFrequency}
                        renderColumnContextMenu={menuProps => menu(menuProps)}
                        resizable
                        sortable={false}
                        showColumnMenuToolOnHover={false}
                        enableColumnAutosize
                        defaultLocked
                      />
                    </div>
                  )}
                </GridFullscreen>
              )}
            </Grid>
          )}
        </Grid>
        <JustifyAbsenceDialog
          open={justifyDialog.open}
          studentName={justifyDialog.studentName}
          lessonLabel={this.buildJustifyLessonLabel(justifyDialog.column)}
          reasons={justificationReasons}
          currentReasonId={justifyDialog.currentReasonId}
          onClose={this.closeJustifyAbsenceDialog}
          onConfirm={this.stageAbsenceJustification}
          onRemove={() => this.stageAbsenceJustification(null)}
        />
      </MuiPickersUtilsProvider>
    );
  }
}

GridFrequency.contextType = AbilityContext;

GridFrequency.propTypes = {
  alert: PropTypes.shape({
    show: PropTypes.func
  }).isRequired,
  match: PropTypes.shape({
    params: PropTypes.shape({
      id: PropTypes.string
    })
  }).isRequired,
  theme: PropTypes.shape({
    palette: PropTypes.shape({
      primary: PropTypes.shape({ main: PropTypes.string.isRequired }).isRequired
    }).isRequired
  }).isRequired,
  jobsState: PropTypes.shape({
    jobs: PropTypes.arrayOf(PropTypes.shape())
  }).isRequired,
  t: PropTypes.func.isRequired,
  i18n: PropTypes.shape({
    language: PropTypes.string
  }).isRequired
};

const mapStateToProps = state => ({
  jobsState: state.jobsState
});

export default connect(mapStateToProps)(
  withTranslation()(withAlert()(withRouter(withTheme(GridFrequency))))
);
